import React, {useEffect, useState} from "react";
import {Alert, Box, Button, Card, Chip, Stack, Typography} from "@mui/material";
import {alpha} from "@mui/material/styles";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import {EmpWebPushRestControllerApiFactory, WebPushStatusDto} from "arteco-api-client-ts";
import {useApiManager} from "../../../components/utils/api";
import {COLOR_VERDE_ARTECO} from "../../../components/layout/colors";
import {panelSx} from "./timeclock_util";
import {isIos, isStandalone} from "../../../components/utils/install_prompt";

export const supported = () => typeof window !== "undefined" && "serviceWorker" in navigator
	&& "PushManager" in window && "Notification" in window;

/** Se avisa cuando cambia la suscripción, para que la tarjeta y el diálogo de #13811 no se contradigan. */
export const PUSH_CHANGED = "arteco-push-changed";

type PushApi = ReturnType<typeof EmpWebPushRestControllerApiFactory>;

/** Pide el permiso y da de alta este dispositivo. Tiene que llamarse desde un clic: iOS no deja pedirlo solo. */
export const subscribePush = async (apiManager: ReturnType<typeof useApiManager>, ctrl: PushApi, publicKey: string) => {
	const permission = await Notification.requestPermission();
	if (permission !== "granted") {
		return undefined;
	}
	const reg = await navigator.serviceWorker.ready;
	const sub = await reg.pushManager.subscribe({userVisibleOnly: true, applicationServerKey: toBytes(publicKey)});
	const json = sub.toJSON();
	const status = await apiManager.execute(ctrl.subscribeWebPush({
		endpoint: sub.endpoint, p256dh: json.keys?.p256dh, auth: json.keys?.auth,
	}));
	window.dispatchEvent(new Event(PUSH_CHANGED));
	return {endpoint: sub.endpoint, status};
};

/** El estado de la suscripción de este dispositivo, preguntado al servidor. */
export const loadPushStatus = (apiManager: ReturnType<typeof useApiManager>, ctrl: PushApi) =>
	navigator.serviceWorker.getRegistration().then(reg => reg?.pushManager.getSubscription())
		.then(sub => apiManager.executeRaw(ctrl.statusWebPush(sub?.endpoint), undefined, true)
			.then(resp => ({endpoint: sub?.endpoint, status: resp.data as WebPushStatusDto})));

/** La clave VAPID llega en base64url; pushManager.subscribe la quiere en bytes. */
const toBytes = (b64url: string) => {
	const b64 = (b64url + "===".slice((b64url.length + 3) % 4)).replace(/-/g, "+").replace(/_/g, "/");
	return Uint8Array.from(atob(b64), c => c.charCodeAt(0));
};


/**
 * Avisos de fichaje en este dispositivo (#13811). Sin activar, los recordatorios siguen llegando por
 * correo. En iPhone solo funciona con la app añadida a la pantalla de inicio.
 */
export const PushToggle = () => {
	const apiManager = useApiManager();
	const ctrl = apiManager.factory(EmpWebPushRestControllerApiFactory);
	const [status, setStatus] = useState<WebPushStatusDto>();
	const [endpoint, setEndpoint] = useState<string>();
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string>();
	const [testResult, setTestResult] = useState<string>();

	useEffect(() => {
		if (!supported()) {
			return;
		}
		const load = () => loadPushStatus(apiManager, ctrl)
			.then(r => {
				setEndpoint(r.endpoint);
				setStatus(r.status);
			})
			.catch(() => undefined);
		load();
		window.addEventListener(PUSH_CHANGED, load);
		return () => window.removeEventListener(PUSH_CHANGED, load);
	}, []);

	if (!supported() || !status?.enabled) {
		return null;
	}

	const activate = async () => {
		setBusy(true);
		setError(undefined);
		try {
			const r = await subscribePush(apiManager, ctrl, status.publicKey || "");
			if (!r) {
				setError("Sin permiso para notificaciones: los avisos te seguirán llegando por correo.");
				return;
			}
			setEndpoint(r.endpoint);
			setStatus(r.status);
		} catch (e) {
			setError("No se han podido activar los avisos en este dispositivo.");
		} finally {
			setBusy(false);
		}
	};

	const test = async () => {
		setBusy(true);
		setTestResult(undefined);
		try {
			const delivered = await apiManager.execute(ctrl.testWebPush());
			setTestResult(delivered
				? "Enviada: en unos segundos te llegará la notificación."
				: "No ha llegado a ningún dispositivo: desactiva y vuelve a activar los avisos.");
		} finally {
			setBusy(false);
		}
	};

	const deactivate = async () => {
		setBusy(true);
		try {
			const reg = await navigator.serviceWorker.ready;
			const sub = await reg.pushManager.getSubscription();
			if (sub) {
				await apiManager.execute(ctrl.unsubscribeWebPush({endpoint: sub.endpoint}));
				await sub.unsubscribe();
			}
			setEndpoint(undefined);
			setStatus({...status, subscribed: false});
		} finally {
			setBusy(false);
		}
	};

	const on = !!(status.subscribed && endpoint);
	const btn = {textTransform: "none", fontWeight: 600, borderRadius: 2} as const;

	return (
		<Card elevation={0} sx={{...panelSx, height: "100%"}}>
			<Stack direction="row" spacing={2} alignItems="flex-start">
				<Box sx={{
					width: 44, height: 44, borderRadius: 2, flexShrink: 0, display: "grid", placeItems: "center",
					bgcolor: alpha(COLOR_VERDE_ARTECO, 0.18), color: "#0f8f76",
				}}>
					<NotificationsActiveRoundedIcon/>
				</Box>
				<Box sx={{flexGrow: 1, minWidth: 0}}>
					<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
						<Typography variant="subtitle1" fontWeight={700}>Avisos en este dispositivo</Typography>
						<Chip size="small" label={on ? "Activados" : "Por correo"} color={on ? "success" : "default"}
							  variant={on ? "filled" : "outlined"}/>
					</Stack>
					<Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>
						{on
							? "Si se te olvida fichar, te llega una notificación aquí en lugar del correo."
							: "Recibe aquí el recordatorio cuando se te olvide fichar; sin activarlo, te llega por correo."}
					</Typography>
				</Box>
			</Stack>
			<Box sx={{mt: 2, pl: {sm: 7.5}}}>
				{on ? (
					<Stack direction="row" spacing={1}>
						<Button size="small" variant="outlined" disabled={busy} onClick={test} sx={btn}>Enviar prueba</Button>
						<Button size="small" color="inherit" disabled={busy} onClick={deactivate} sx={btn}>Desactivar</Button>
					</Stack>
				) : isIos() && !isStandalone() ? (
					<Alert severity="info" sx={{borderRadius: 2}}>
						En iPhone, primero añade esta página a la pantalla de inicio (Compartir › Añadir a
						pantalla de inicio) y ábrela desde ahí.
					</Alert>
				) : (
					<Button size="small" variant="contained" disableElevation disabled={busy} onClick={activate} sx={btn}>
						Activar avisos
					</Button>
				)}
				{testResult && <Alert severity="info" sx={{mt: 1.5, borderRadius: 2}}>{testResult}</Alert>}
				{error && <Alert severity="warning" sx={{mt: 1.5, borderRadius: 2}}>{error}</Alert>}
			</Box>
		</Card>
	);
};
